import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { BedDouble, Bath, Building2, Receipt, Play, X, Ruler, Images } from "lucide-react";
import { PROPERTY, TEASER_IMAGES } from "@/data/property";
import { useSite } from "@/context/SiteContext";
import { scrollToId } from "@/lib/smooth";
import { EASE } from "@/components/SectionHeader";

function MaskedLine({ children, delay = 0 }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className="block"
        initial={{ y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function TeaserModal({ onClose, images }) {
  const [slide, setSlide] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % images.length), 3500);
    return () => clearInterval(t);
  }, [images.length]);
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <button
        data-testid="teaser-close-button"
        aria-label="Close teaser"
        onClick={onClose}
        className="absolute right-5 top-5 rounded-full border border-white/30 p-2 text-white transition-colors duration-200 hover:bg-white/10"
      >
        <X size={20} />
      </button>
      <div
        className="relative w-full max-w-5xl overflow-hidden rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <AnimatePresence mode="wait">
          <motion.img
            key={slide}
            src={images[slide]}
            alt="461 Beechwood still"
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.1, ease: "easeOut" }}
            className="aspect-video w-full object-cover"
          />
        </AnimatePresence>
        <div className="absolute bottom-4 left-4 rounded-full bg-black/60 px-4 py-1.5 text-[11px] uppercase tracking-[0.22em] text-white backdrop-blur-sm">
          {String(slide + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")} — Photo Teaser
        </div>
      </div>
    </motion.div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const { formatPrice, media, heroUrl, openFloorPlans, setFlyoutOpen } = useSite();
  const [teaserOpen, setTeaserOpen] = useState(false);

  const heroImg = heroUrl || PROPERTY.heroImage;
  const teaserImages = media.length
    ? [heroImg, ...media.filter((m) => m.url !== heroImg).map((m) => m.url)].slice(0, 6)
    : TEASER_IMAGES;

  const specs = [
    { icon: BedDouble, label: `${PROPERTY.specs.bedrooms} Bedrooms` },
    { icon: Bath, label: `${PROPERTY.specs.bathrooms} Bathrooms` },
    { icon: Building2, label: PROPERTY.specs.type },
    { icon: Receipt, label: `Taxes ${PROPERTY.specs.taxes}` },
  ];

  return (
    <section
      id="overview"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-end overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img
          src={heroImg}
          alt="461 Beechwood Place, Waterloo"
          className="h-full w-full scale-[1.12] object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20" />
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-20 pt-36 sm:px-6 sm:pb-8 lg:px-8">
        <div className="max-w-3xl text-white">
          <MaskedLine delay={0.05}>
            <p className="mb-5 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-white/80">
              <span className="h-px w-10 bg-bronze" />
              Exclusive Listing — Waterloo · Ontario
            </p>
          </MaskedLine>
          <h1
            data-testid="hero-title"
            className="font-display text-5xl font-extrabold uppercase leading-[0.95] tracking-tight sm:text-7xl lg:text-8xl"
          >
            <MaskedLine delay={0.15}>52-461 Beechwood Place</MaskedLine>
          </h1>
          <MaskedLine delay={0.3}>
            <p className="mt-5 max-w-xl font-serif text-xl italic text-white/85 sm:text-2xl">
              {PROPERTY.tagline}.
            </p>
          </MaskedLine>
          <MaskedLine delay={0.42}>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-4">
              <button
                data-testid="hero-cta-tour"
                onClick={() => scrollToId("contact")}
                className="rounded-full bg-white px-7 py-3 text-sm font-semibold text-zinc-900 transition-colors duration-200 hover:bg-bronze hover:text-white"
              >
                Schedule Private Tour
              </button>
              <button
                data-testid="teaser-video-button"
                onClick={() => setTeaserOpen(true)}
                className="flex items-center gap-2 rounded-full border border-white/40 px-7 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-white/10"
              >
                <Play size={15} /> Watch Photo Teaser
              </button>
              <button
                data-testid="hero-floorplans-button"
                onClick={() => openFloorPlans()}
                className="flex items-center gap-2 text-sm font-semibold text-white/90 underline decoration-bronze decoration-2 underline-offset-4 transition-colors duration-200 hover:text-white"
              >
                <Ruler size={15} /> View Floor Plans
              </button>
              <button
                data-testid="hero-gallery-button"
                onClick={() => setFlyoutOpen(true)}
                className="flex items-center gap-2 text-sm font-semibold text-white/90 underline decoration-bronze decoration-2 underline-offset-4 transition-colors duration-200 hover:text-white"
              >
                <Images size={15} /> View Photo Gallery
              </button>
            </div>
          </MaskedLine>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.8, ease: EASE }}
          className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4 rounded-2xl border border-white/20 bg-white/10 px-6 py-5 text-white backdrop-blur-xl"
        >
          <p data-testid="hero-price-badge" className="font-display text-2xl font-extrabold sm:text-3xl">
            {PROPERTY.priceCad ? formatPrice(PROPERTY.priceCad) : "Contact Agent"}
          </p>
          <span className="hidden h-6 w-px bg-white/25 sm:block" />
          {specs.map(({ icon: Icon, label }) => (
            <span key={label} className="flex items-center gap-2 text-sm font-medium">
              <Icon size={16} className="text-bronze" />
              {label}
            </span>
          ))}
          <p className="ml-auto hidden text-xs uppercase tracking-[0.2em] text-white/70 xl:block">
            {PROPERTY.address}
          </p>
        </motion.div>
      </div>

      <AnimatePresence>
        {teaserOpen && <TeaserModal onClose={() => setTeaserOpen(false)} images={teaserImages} />}
      </AnimatePresence>
    </section>
  );
}
